import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ControlledSelectField } from 'components/Form';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { cnMixSpace, IconQuestion } from 'uikit';

import { useMemo } from 'react';
import type { Control } from 'react-hook-form';

import {
    LIMITED_COST_TYPE_PLACEHOLDER,
    OTHER_COST_TYPE_PLACEHOLDER,
} from '../../../CreateOrEditOtherVariable/constants';
import {
    emptyCostTypeMessage,
    emptyCostTypeOptionId,
    limitedCostTypeTooltip,
    otherCostTypeTooltip,
} from '../../catalogOtherAndLimitedHelpers';
import { useCatalogCostCandidateTypes } from '../../hooks/useCatalogCostCandidateTypes';
import { CatalogOtherAndLimitedTab } from '../../types';
import type { TCatalogCostFormValues, TCatalogCostTypeOption } from '../../types';

import Styles from './createOrEditCatalogCostModal.module.css';

type TCatalogCostTypeSelectProps = {
    control: Control<TCatalogCostFormValues>;
    tab: CatalogOtherAndLimitedTab;
    methodology: ProjectMethodologyEnum;
    currentTypeName?: string;
    currentTypeUniqueId?: string;
    currentItemId?: number;
    isTooltipHidden?: boolean;
};

const EMPTY_TYPE_OPTION: TCatalogCostTypeOption = {
    id: emptyCostTypeOptionId,
    name: emptyCostTypeMessage,
};

const getItemLabel = (item: TCatalogCostTypeOption) => item.name;
const getItemDisabled = (item: TCatalogCostTypeOption) => item.id === EMPTY_TYPE_OPTION.id;

export const CatalogCostTypeSelect = ({
    control,
    tab,
    methodology,
    currentTypeName,
    currentTypeUniqueId,
    currentItemId,
    isTooltipHidden,
}: TCatalogCostTypeSelectProps) => {
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;
    const {
        items: candidateItems,
        isLoading,
        systemTypeIds,
    } = useCatalogCostCandidateTypes({
        tab,
        methodology,
        excludeId: currentItemId,
    });

    const label = isOthersTab ? OTHER_COST_TYPE_PLACEHOLDER : LIMITED_COST_TYPE_PLACEHOLDER;
    const tooltipText = isOthersTab ? otherCostTypeTooltip : limitedCostTypeTooltip;

    const items = useMemo(() => {
        const availableItems = [...candidateItems];

        if (currentTypeUniqueId && currentTypeName && !availableItems.some((item) => item.id === currentTypeUniqueId)) {
            availableItems.unshift({
                id: currentTypeUniqueId,
                name: currentTypeName,
                isSystem: systemTypeIds.has(currentTypeUniqueId),
            });
        }

        if (!availableItems.length) {
            return [EMPTY_TYPE_OPTION];
        }

        return availableItems;
    }, [candidateItems, currentTypeName, currentTypeUniqueId, systemTypeIds]);

    const hasAvailableTypes = items.some((item) => item.id !== EMPTY_TYPE_OPTION.id);
    const isCreating = typeof currentItemId !== 'number';
    const matchedTypeIsSystem = candidateItems.some((item) => item.id === currentTypeUniqueId && item.isSystem);
    const isListedSystemType = Boolean(currentTypeUniqueId && systemTypeIds.has(currentTypeUniqueId));
    const isTypeLocked = !isCreating && (matchedTypeIsSystem || isListedSystemType);

    return (
        <ControlledSelectField
            name="costType"
            disabled={isTypeLocked}
            label={
                (
                    <div className={Styles.centeredLabel}>
                        <span>{label}</span>
                        <span className="FieldLabel-Star">*</span>
                        <ComponentWithTooltip
                            tooltipText={tooltipText}
                            className={`${cnMixSpace({ mL: '2xs' })} ${Styles.centeredLabel}`}
                        >
                            <IconQuestion view="secondary" />
                        </ComponentWithTooltip>
                    </div>
                ) as any
            }
            placeholder={hasAvailableTypes ? label : emptyCostTypeMessage}
            control={control}
            items={items}
            getItemLabel={getItemLabel}
            getItemDisabled={getItemDisabled}
            rules={{ required: 'Обязательное поле' }}
            isTooltipHidden={isTooltipHidden}
            isLoading={isLoading}
        />
    );
};

CatalogCostTypeSelect.displayName = 'CatalogCostTypeSelect';
